// Derives the offer/budget summary-card rows from each template's #show payload.
// Kept as pure functions so the money formatting (the error-prone part) is unit-
// tested without rendering.

export const OPERATOR_LABEL = { OR: 'vs', AND: '+' };

export const REVENUE_BASE_LABEL = {
  default: 'Default',
  gross: 'Gross',
  net_gross: 'Net Gross',
  adjusted_gross: 'Adjusted Gross',
};

const pluralize = (count, singular, plural) => (Number(count) === 1 ? singular : plural);

// Whole-dollar money, matching the mockup ("$1,500", "$25"). null when blank/NaN
// so callers can omit the row.
export const formatUsd = (value) => {
  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) return null;
  return `$${Math.round(Number(value)).toLocaleString('en-US')}`;
};

const pct = (value) => (value === null || value === undefined || value === '' ? null : `${Number(value)}%`);

export const offerSummaryRows = (data) => {
  if (!data) return [];
  const summary = data.summary || {};
  const rows = [];

  // A $0 (or absent) guarantee is a door deal, not a "$0 vs …" — treat it as no
  // amount so the operator never renders alone.
  const amount = Number(data.dollar_amount) > 0 ? formatUsd(data.dollar_amount) : null;
  const upsidePct = Number(data.upside_percentage) ? `${Number(data.upside_percentage)}%` : null;
  const operator = OPERATOR_LABEL[data.dollar_or_percentage_operator] || summary.operator_label || '';
  const base = REVENUE_BASE_LABEL[data.revenue_base];
  const upsidePhrase = upsidePct ? `${upsidePct}${base ? ` of ${base}` : ''}` : null;

  if (amount && upsidePhrase) {
    rows.push({ label: 'Deal', value: `${amount} ${operator} ${upsidePhrase}`.replace(/\s+/g, ' ').trim() });
  } else if (amount) {
    rows.push({ label: 'Deal', value: amount });
  } else if (upsidePhrase) {
    rows.push({ label: 'Deal', value: upsidePhrase });
  }

  if (data.has_merchandising) {
    const soft = pct(data.soft_merch_rate);
    const hard = pct(data.hard_merch_rate);
    const merch = [soft && `${soft} soft`, hard && `${hard} hard`].filter(Boolean).join(' / ');
    if (merch) rows.push({ label: 'Merch', value: merch });
  }

  rows.push({
    label: 'Extras',
    value: `${summary.bonus_count || 0} ${pluralize(summary.bonus_count, 'bonus', 'bonuses')} · ${summary.deposit_count || 0} ${pluralize(summary.deposit_count, 'deposit', 'deposits')} · ${summary.deal_term_count || 0} ${pluralize(summary.deal_term_count, 'term', 'terms')}`,
  });

  return rows;
};

export const budgetSummaryRows = (lineItems) => {
  if (!lineItems) return [];
  const inCategory = (category) => lineItems.filter((li) => li.category === category);

  const tickets = inCategory('TICKET');
  const ticketPrices = tickets
    .filter((t) => t.show_in_offer !== false && Number(t.left_hand) > 0)
    .map((t) => Number(t.left_hand));
  let ticketValue = `${tickets.length} ${pluralize(tickets.length, 'tier', 'tiers')}`;
  if (ticketPrices.length) {
    const min = Math.min(...ticketPrices);
    const max = Math.max(...ticketPrices);
    ticketValue += ` · ${min === max ? formatUsd(min) : `${formatUsd(min)}–${formatUsd(max)}`}`;
  }

  // A template has no live sales/gross, so percentage and per-ticket lines can't
  // resolve to a dollar figure — only `fixed` lines (left_hand × right_hand) can.
  // Summing the others would multiply a percentage as if it were dollars.
  const fixedDollarTotal = (category) => inCategory(category)
    .filter((li) => li.calc_type === 'fixed')
    .reduce((sum, li) => sum + Number(li.left_hand || 0) * Number(li.right_hand || 0), 0);

  const countRow = (category, label) => {
    const rows = inCategory(category);
    let value = `${rows.length} ${pluralize(rows.length, 'line', 'lines')}`;
    const total = fixedDollarTotal(category);
    if (total > 0) value += ` · ${formatUsd(total)}`;
    return { label, value };
  };

  return [
    { label: 'Ticket tiers', value: ticketValue },
    countRow('EXPENSES', 'Expenses'),
    countRow('INCOME', 'Ancillary income'),
  ];
};
